import {
  BlurEvent,
  ChangeEvent,
  ChangeEventHandler,
  FieldHandler,
  getRendererFromDescriptor,
  IPageBuilderContext,
  PageBuilderContext,
  useCurrentStepIndex,
  useField,
  useForm,
} from '@kizi/comparator-core';
import React, { useContext, useMemo } from 'react';
import { uniq } from 'lodash';

export type FieldProps<P = any> = {
  fieldName: string;
  type?: string;
} & P;

const getEventListener =
  (
    send: ReturnType<typeof useForm>['send'],
    type?: 'CHANGE' | 'blur'
  ): ChangeEventHandler =>
  (e) => {
    if (send) {
      if (e.persist && typeof e.persist === 'function') e.persist();
      send({
        type: type ?? e.type,
        target: { name: e.target.name, value: e.target.value },
      } as BlurEvent | ChangeEvent);
    }
  };

const getFieldProps = (
  fieldProps: Omit<FieldHandler, 'type'>,
  inputProps: Omit<FieldProps, 'fieldName'>,
  send: ReturnType<typeof useForm>['send']
) => {
  const name = fieldProps ? fieldProps.name : undefined;
  const value = fieldProps ? fieldProps.value : undefined;
  const _error =
    fieldProps && fieldProps.errors
      ? uniq(fieldProps.errors).join('\n')
      : undefined;
  const active = fieldProps?.active !== false;
  const _label =
    inputProps._label !== undefined ? inputProps._label : fieldProps?.label;
  const _options = fieldProps ? fieldProps.options : undefined;
  const onChange: ChangeEventHandler = getEventListener(send, 'CHANGE');
  const onBlur = getEventListener(send);
  const metaProps =
    fieldProps && fieldProps.meta ? fieldProps.meta.props : undefined;
  const propMap = inputProps?.propMap ? { propMap: inputProps.propMap } : {};
  return {
    name,
    value,
    _error,
    active,
    _label,
    _options,
    onChange,
    onBlur,
    ...metaProps,
    ...inputProps,
    ...propMap,
  };
};
export const Field: React.FC<React.PropsWithChildren<FieldProps>> = ({
  fieldName,
  children,
  type: typeProp,
  ...inputProps
}) => {
  /**
   * for some reason in some edge cases the underlying send function used in fieldDef.onChange and field.onBlur
   * can return an invalid send implementation which is why I get it directly from the active step with useForm instead
   * to investigate...
   */
  const fieldDef = useField(fieldName);
  const currentIndex = useCurrentStepIndex();
  const { send } = useForm(currentIndex ?? '');
  const type = typeProp ?? fieldDef?.type;
  const { loadFn } = useContext<IPageBuilderContext>(PageBuilderContext);
  return useMemo(() => {
    if (!fieldDef) {
      console.warn('could not find field:', fieldName, '. Rendering skipped.');
      return null;
    }
    if (!type) {
      console.warn(fieldName, ': has no type. Rendering skipped.');
      return null;
    }
    if (!loadFn) {
      console.warn(
        fieldName +
          ': could not find loadnFn or PageBuilderContext ancestor. Rendering skipped.'
      );
      return null;
    }
    const { type: _type, ...fieldProps } = fieldDef;
    const props = getFieldProps(fieldProps, inputProps, send);

    //temporary fix to enable feild_446, field_230 and field_797 on apply page
    if (
      fieldName === 'samenstelling.field_446' ||
      fieldName === 'samenstelling.field_230' ||
      fieldName === 'samenstelling.field_797'
    ) {
      fieldProps.active = true;
    }

    return (
      <>
        {fieldProps.active !== false &&
          getRendererFromDescriptor({
            type,
            props: { ...props, children },
          })}
      </>
    );
  }, [
    fieldDef,
    loadFn,
    inputProps,
    children,
    type,
    fieldName,
    currentIndex,
    send,
  ]);
};
